"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type RefObject, type ReactNode } from "react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Utilities & Hooks
// ---------------------------------------------------------------------------

const delayClassMap = {
  0: "delay-0",
  10: "delay-100",
  15: "delay-150",
  20: "delay-200",
  30: "delay-300",
} as const;

function getDelayClass(delay: number) {
  return delayClassMap[Math.round(delay * 100) as keyof typeof delayClassMap] ?? "delay-0";
}

function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible] as const;
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();
  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0 transition-all duration-[650ms] ease-out",
        fromClasses[from],
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Service = {
  n: string;
  title: string;
  body: string;
  img: string;
  tags: string[];
};

type ToolRow = {
  focus: string;
  tools: string;
};

type Benefit = {
  title: string;
  body: string;
};

type StepData = {
  n: string;
  title: string;
  body: string;
};

type Audience = {
  title: string;
  body: string;
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const HERO_TAGS = ["GTM Strategy", "Brand Identity", "Social Media", "Content", "PR & ORM"];

const TRUST = [
  "End-to-End Brand Building — Strategy to Execution",
  "Dedicated Creative & Strategy Team Per Client",
  "Data-Driven Campaigns with Monthly Reporting",
  "Compounding Growth — Brand Equity That Lasts",
];

const SERVICES: Service[] = [
  {
    n: "01",
    title: "GTM Strategy (Go-To-Market)",
    body: "A sharp, actionable go-to-market plan that defines your target audience, competitive positioning, pricing model and launch roadmap. We align your product, message and channels so your brand enters the market with clarity and momentum — not guesswork.",
    img: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=600&q=80",
    tags: ["Audience Targeting", "Competitive Positioning", "Launch Roadmap"],
  },
  {
    n: "02",
    title: "Brand Identity",
    body: "A complete brand system built from the ground up — logo, color palette, typography, tone of voice, brand guidelines and visual storytelling. We craft identities that are instantly recognizable, emotionally resonant and built to scale across every platform and touchpoint.",
    img: "https://images.unsplash.com/photo-1626785774573-4b799315345d?w=600&q=80",
    tags: ["Logo & Visual System", "Brand Guidelines", "Tone of Voice"],
  },
  {
    n: "03",
    title: "Social Media Marketing",
    body: "Data-driven social media strategies across Instagram, LinkedIn, Facebook, YouTube and X. From content calendars and reel production to paid campaigns and community management — we build engaged audiences that trust your brand and convert into customers.",
    img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=600&q=80",
    tags: ["Content Calendars", "Paid Campaigns", "Community Management"],
  },
  {
    n: "04",
    title: "Content Strategy",
    body: "Strategic content that educates, attracts and converts. We build end-to-end content ecosystems including SEO blogs, thought leadership articles, video scripts, email sequences, case studies and whitepapers — designed to rank, engage and nurture your audience through every funnel stage.",
    img: "https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600&q=80",
    tags: ["SEO Blogs", "Thought Leadership", "Email Sequences"],
  },
  {
    n: "05",
    title: "Web Design & Development",
    body: "High-converting, brand-aligned websites and landing pages that make a powerful first impression. We combine stunning UI design, fast-loading architecture, mobile responsiveness and CRO best practices to turn your website into your hardest-working sales asset.",
    img: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&q=80",
    tags: ["UI Design", "CRO", "Mobile-First"],
  },
  {
    n: "06",
    title: "PR & ORM",
    body: "Control your narrative and protect your brand's digital reputation. We handle press releases, media outreach, influencer partnerships and ORM campaigns — suppressing negative content, amplifying positive coverage and building the credibility that makes prospects choose you over competitors.",
    img: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&q=80",
    tags: ["Media Outreach", "Reputation Management", "Influencer Partnerships"],
  },
];

const TOOLS: ToolRow[] = [
  { focus: "GTM & Analytics", tools: "Google Analytics 4, Mixpanel, HubSpot, Notion, Miro" },
  { focus: "Brand Design", tools: "Figma, Adobe Illustrator, Canva Pro, Adobe Photoshop" },
  { focus: "Social Media", tools: "Meta Business Suite, Hootsuite, Buffer, Later, Sprout Social" },
  { focus: "Content & SEO", tools: "Ahrefs, SEMrush, Surfer SEO, Jasper, Notion, WordPress" },
  { focus: "Web Design", tools: "Webflow, WordPress, Elementor, Framer, Next.js, Shopify" },
  { focus: "PR & ORM", tools: "Meltwater, Mention, Google Alerts, BuzzSumo, Prowly" },
];

const BENEFITS: Benefit[] = [
  {
    title: "Top-of-Funnel Dominance",
    body: "Appear where your audience is searching, scrolling and deciding — before they ever visit a competitor.",
  },
  {
    title: "Stronger Brand Recall",
    body: "A consistent identity across every channel builds familiarity, which drives preference and long-term loyalty.",
  },
  {
    title: "Organic Lead Generation",
    body: "SEO, content and social strategies that attract high-intent prospects without relying solely on paid ads.",
  },
  {
    title: "Premium Positioning",
    body: "Strategic brand building allows you to charge more, attract better clients and compete on value — not price.",
  },
  {
    title: "Trust & Credibility",
    body: "Positive PR, thought leadership and ORM establish your brand as the go-to authority in your category.",
  },
  {
    title: "Compounding Growth",
    body: "Unlike ads that stop when budgets do, brand equity, content and reputation grow in value over time.",
  },
];

const STEPS: StepData[] = [
  { n: "01", title: "Discovery & Audit", body: "In-depth analysis of your current brand, digital presence, competitors and target audience to uncover gaps and opportunities." },
  { n: "02", title: "Strategy & Positioning", body: "Define your unique value proposition, brand voice, messaging framework and channel strategy aligned to your business goals." },
  { n: "03", title: "Brand & Creative Development", body: "Logo, visual identity, brand guidelines, website design and content templates — everything built for consistency and impact." },
  { n: "04", title: "Content & Campaign Execution", body: "Launch SEO content, social media campaigns, PR outreach and paid amplification with a clear editorial calendar and KPIs." },
  { n: "05", title: "Community & Reputation Building", body: "Engage your growing audience, respond to sentiment, manage reviews and build relationships that convert followers into advocates." },
  { n: "06", title: "Measure, Optimise & Scale", body: "Monthly performance reporting, A/B testing and continuous refinement to improve ROI and scale what is working." },
];

const AUDIENCES: Audience[] = [
  {
    title: "Startups & New Ventures",
    body: "Launching with a strong brand foundation, GTM plan and digital presence that attracts early customers and investors.",
  },
  {
    title: "SMEs Ready to Scale",
    body: "Businesses that have product-market fit and need visibility, positioning and content to grow beyond referrals.",
  },
  {
    title: "Established Brands",
    body: "Companies looking to reposition, refresh their identity, or expand into new markets and demographics.",
  },
  {
    title: "Founders & Personal Brands",
    body: "Entrepreneurs, consultants and executives building thought leadership and authority in their space.",
  },
];

// ---------------------------------------------------------------------------
// Primitive Components
// ---------------------------------------------------------------------------

function Divider() {
  return <div className="w-11 h-[3px] bg-[#1565C0] mb-6" />;
}

function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block", className)}>
      {children}
    </span>
  );
}

function CheckIcon() {
  return (
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#1565C0] text-white flex-shrink-0">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section Components
// ---------------------------------------------------------------------------

function ServiceCard({ service, index }: { service: Service; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <article
      ref={ref}
      className={cn(
        "group flex flex-col overflow-hidden rounded-sm border border-[#E2E8F0] bg-white shadow-[0_1px_4px_rgba(0,0,0,0.04)] transition-all duration-500 ease-out hover:border-[#CBD5E1] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)]",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="relative h-[170px] overflow-hidden">
        <Image
          src={service.img}
          alt={service.title}
          fill
          className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 rounded-sm bg-[#1565C0] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white">
          {service.n}
        </span>
      </div>
      <div className="flex flex-col p-[28px] pb-[32px]">
        <h3 className="text-[18px] font-bold text-[#0F172A] mb-2 leading-[1.25]">{service.title}</h3>
        <p className="text-[13.5px] text-[#64748B] mb-5 leading-[1.78]">{service.body}</p>
        <div className="mt-auto flex flex-wrap gap-2">
          {service.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-[#EFF6FF] px-2.5 py-1 text-[11px] font-semibold text-[#1565C0]">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

function ToolRow({ row, index }: { row: ToolRow; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "grid grid-cols-[1fr_1.8fr] gap-4 px-4 py-4 border-b border-[#E2E8F0] items-center transition-all duration-500 ease-out",
        index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"
      )}
    >
      <span className="text-[13px] font-semibold text-[#0F172A]">{row.focus}</span>
      <span className="text-[13px] leading-[1.5] text-[#64748B]">{row.tools}</span>
    </div>
  );
}

function BenefitCard({ benefit, index }: { benefit: Benefit; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "bg-[#F8FAFC] border-t-4 border-[#1565C0] p-7 transition-all duration-500 ease-out",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <h3 className="text-[14px] font-semibold text-[#0F172A] mb-2.5">{benefit.title}</h3>
      <p className="text-[13.5px] leading-[1.85] text-[#64748B]">{benefit.body}</p>
    </div>
  );
}

function Step({ step, index, last }: { step: StepData; index: number; last: boolean }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex gap-4 items-start pb-7 transition-all duration-[550ms] ease-out",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      {!last && <span className="absolute left-[17px] top-[36px] bottom-0 w-px bg-[#E2E8F0]" />}
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] text-[14px] font-bold text-white flex-shrink-0">
        {step.n}
      </div>
      <div className="pt-1">
        <h4 className="text-[14px] font-semibold text-[#0F172A] mb-2">{step.title}</h4>
        <p className="text-[13.5px] leading-[1.85] text-[#64748B]">{step.body}</p>
      </div>
    </div>
  );
}

function AudienceCard({ audience, index }: { audience: Audience; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "flex gap-4 items-start p-7 border border-[#E2E8F0] bg-white transition-all duration-500 ease-out hover:border-[#CBD5E1] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)]",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EFF6FF] text-[13px] font-bold text-[#1565C0] flex-shrink-0">
        {String(index + 1).padStart(2, "0")}
      </div>
      <div>
        <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1.5">{audience.title}</h3>
        <p className="text-[13.5px] leading-[1.85] text-[#64748B]">{audience.body}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function GrowthBrandingPage() {
  return (
    <main className="bg-white">

      {/* ── HERO ── */}
      <section className="grid lg:grid-cols-2 min-h-0 lg:min-h-[600px]">
        <div className="flex flex-col justify-center bg-white py-[90px] pl-4 sm:pl-8 lg:pl-[80px] pr-4 sm:pr-8 lg:pr-[64px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] font-medium text-[#64748B] mb-5">
            <Link href="/services" className="text-[#1565C0] hover:text-[#0D47A1] transition-colors duration-200">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <Link href="/services/it" className="text-[#1565C0] hover:text-[#0D47A1] transition-colors duration-200">
              IT Solutions
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span className="text-[#0F172A]">Growth & Branding</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {HERO_TAGS.map((tag) => (
              <span key={tag} className="rounded-full bg-[#EFF6FF] px-3 py-1 text-[11px] font-semibold text-[#1565C0]">
                {tag}
              </span>
            ))}
          </div>

          <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block">
            IT Solutions — Growth & Branding
          </span>
          <Divider />
          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-[#0F172A] leading-[1.1] mb-6 max-w-[44rem]">
            Build a Brand That Gets Found, Trusted and Chosen
          </h1>
          <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10 max-w-[480px]">
            From strategy to story — we help businesses define their identity, dominate their niche and grow their audience with proven branding, content and digital marketing frameworks that turn visibility into revenue.
          </p>

          <div className="flex flex-col gap-3 mb-9">
            {TRUST.map((text, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckIcon />
                <p className="text-[13.5px] leading-[1.78] text-[#334155]">{text}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <button className="rounded-sm bg-[#1565C0] px-7 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
              START YOUR BRAND
            </button>
            <button className="rounded-sm border border-[#E2E8F0] bg-white px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
              VIEW CASE STUDIES
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="https://images.unsplash.com/photo-1493421419110-74f4e85ba126?w=900&q=85"
            alt="Growth and branding"
            fill
            className="object-cover"
          />
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { v: "6", l: "Service Areas" },
          { v: "90-Day", l: "GTM Roadmap" },
          { v: "6", l: "Tool Categories" },
          { v: "4", l: "Audience Segments" },
        ].map((item, index) => (
          <div key={index} className={cn("py-5 text-center", index < 3 ? "xl:border-r border-white/15" : "")}>
            <div className="text-[22px] font-black text-white leading-none">{item.v}</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70 mt-1">{item.l}</div>
          </div>
        ))}
      </div>

      {/* ── SERVICES ── */}
      <section className="py-12 sm:py-16 lg:py-[96px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-[56px]">
              <Eyebrow>Growth & Branding Services</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Six Capabilities. <br />One Unified Brand System.
                </h2>
                <p className="text-[14px] leading-[1.75] text-[#64748B] max-w-[360px]">
                  Every service is engineered to work together — building brand equity that compounds over time.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-6 xl:grid-cols-3 md:grid-cols-2">
            {SERVICES.map((service, index) => (
              <ServiceCard key={service.n} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ── TOOLS & PROCESS ── */}
      <section className="py-12 sm:py-16 lg:py-[96px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-start">

          {/* Tools table */}
          <Reveal from="left">
            <div>
              <Eyebrow>Tools & Platforms We Use</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,2.8vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                Best-in-Class Tools. <br />Proven Workflows.
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
                We use the right tool for each job — combining industry-leading platforms to deliver precision at every stage of the brand-building process.
              </p>
              <div className="grid grid-cols-[1fr_1.8fr] bg-[#1565C0] px-4 py-3 text-white">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em]">Focus Area</span>
                <span className="text-[11px] font-bold uppercase tracking-[0.1em]">Tools & Technologies</span>
              </div>
              <div className="mt-1 overflow-hidden rounded-b-xl">
                {TOOLS.map((row, index) => (
                  <ToolRow key={row.focus} row={row} index={index} />
                ))}
              </div>
            </div>
          </Reveal>

          {/* Process steps */}
          <Reveal from="right" delay={0.1}>
            <div>
              <Eyebrow>Our 6-Stage Process</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                6-Stage Growth & Branding Process
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10">
                A structured process from first audit to full-scale execution — with transparent reporting and continuous optimisation at every stage.
              </p>
              <div className="space-y-0">
                {STEPS.map((step, index) => (
                  <Step key={step.n} step={step} index={index} last={index === STEPS.length - 1} />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="py-12 sm:py-16 lg:py-[96px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-[56px]">
              <Eyebrow>Business Benefits</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Why Growth & Branding <br />Moves the Needle
                </h2>
                <p className="text-[14px] leading-[1.75] text-[#64748B] max-w-[360px]">
                  Strategic brand building delivers returns that paid advertising alone never can.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {BENEFITS.map((benefit, index) => (
              <BenefitCard key={benefit.title} benefit={benefit} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IS THIS FOR ── */}
      <section className="py-12 sm:py-16 lg:py-[96px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Who Is This For</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                Built for Businesses <br />at Every Growth Stage
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10">
                Whether you are launching, scaling, repositioning, or building personal authority — our growth and branding services are structured to meet you where you are.
              </p>
              <div className="relative w-full h-[260px] overflow-hidden rounded-sm">
                <Image
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80"
                  alt="Business growth team"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="flex flex-col gap-3">
              {AUDIENCES.map((audience, index) => (
                <AudienceCard key={audience.title} audience={audience} index={index} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-12 sm:py-16 lg:py-[96px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px] overflow-hidden rounded-sm">
              <video
                src="/videos/CTA.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              <Eyebrow>Get Started</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(26px,3vw,40px)] font-bold text-[#0F172A] leading-[1.15] mb-4">
                Ready to Build a Brand <br />That Drives Revenue?
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-9">
                Book a free brand discovery session. We assess your current positioning, identify growth opportunities and present a clear roadmap tailored to your business — at no cost and no obligation.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="rounded-sm bg-[#1565C0] px-8 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
                  BOOK FREE DISCOVERY CALL
                </button>
                <button className="rounded-sm border border-[#E2E8F0] bg-white px-8 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
                  VIEW CASE STUDIES
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}